/**
 * Canvas Resize Hook.
 *
 * Encapsulates resize handle logic for yard sections. Supports all 8
 * handle positions (4 corners + 4 midpoints) with proper minimum size
 * enforcement and position adjustment for top/left handles.
 */

import { useState, useCallback, useRef } from "react";
import type { HandlePosition } from "./ResizeHandles";

const MIN_WIDTH = 50;
const MIN_HEIGHT = 40;

interface ResizeState {
  sectionId: string;
  handle: HandlePosition;
  startX: number;
  startY: number;
  origX: number;
  origY: number;
  origW: number;
  origH: number;
}

interface UseCanvasResizeOptions {
  yardWidth: number;
  yardHeight: number;
  onResizeEnd: (
    sectionId: string,
    x: number,
    y: number,
    width: number,
    height: number,
  ) => void;
}

export function useCanvasResize({
  yardWidth,
  yardHeight,
  onResizeEnd,
}: UseCanvasResizeOptions) {
  const [resizeOffset, setResizeOffset] = useState<{
    id: string;
    dx: number;
    dy: number;
    dw: number;
    dh: number;
  } | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef<ResizeState | null>(null);

  const handleResizePointerDown = useCallback(
    (
      sectionId: string,
      handle: HandlePosition,
      x: number,
      y: number,
      width: number,
      height: number,
      e: React.PointerEvent,
    ) => {
      e.stopPropagation();
      e.preventDefault();

      const svg = (e.target as SVGElement).ownerSVGElement;
      if (!svg) return;

      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const svgPt = pt.matrixTransform(svg.getScreenCTM()?.inverse());

      resizeRef.current = {
        sectionId,
        handle,
        startX: svgPt.x,
        startY: svgPt.y,
        origX: x,
        origY: y,
        origW: width,
        origH: height,
      };
      setIsResizing(true);
      (e.target as SVGElement).setPointerCapture(e.pointerId);
    },
    [],
  );

  const handleResizePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!resizeRef.current) return;

      const svg =
        (e.target as SVGElement).ownerSVGElement ??
        (e.target as unknown as SVGSVGElement);
      if (!svg || !("createSVGPoint" in svg)) return;

      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const svgPt = pt.matrixTransform(svg.getScreenCTM()?.inverse());

      const r = resizeRef.current;
      const deltaX = svgPt.x - r.startX;
      const deltaY = svgPt.y - r.startY;

      let dx = 0,
        dy = 0,
        dw = 0,
        dh = 0;

      // Horizontal component
      if (r.handle.includes("w")) {
        dx = Math.min(deltaX, r.origW - MIN_WIDTH);
        dw = -dx;
      } else if (r.handle.includes("e")) {
        dw = Math.max(deltaX, MIN_WIDTH - r.origW);
      }

      // Vertical component
      if (r.handle.startsWith("n")) {
        dy = Math.min(deltaY, r.origH - MIN_HEIGHT);
        dh = -dy;
      } else if (r.handle.startsWith("s")) {
        dh = Math.max(deltaY, MIN_HEIGHT - r.origH);
      }

      // Clamp to yard boundaries
      const newX = Math.max(0, r.origX + dx);
      const newY = Math.max(0, r.origY + dy);
      const newW = Math.min(r.origW + dw, yardWidth - newX);
      const newH = Math.min(r.origH + dh, yardHeight - newY);

      setResizeOffset({
        id: r.sectionId,
        dx: newX - r.origX,
        dy: newY - r.origY,
        dw: newW - r.origW,
        dh: newH - r.origH,
      });
    },
    [yardWidth, yardHeight],
  );

  const handleResizePointerUp = useCallback(() => {
    if (!resizeRef.current || !resizeOffset) {
      resizeRef.current = null;
      setIsResizing(false);
      return;
    }

    const r = resizeRef.current;
    onResizeEnd(
      r.sectionId,
      r.origX + resizeOffset.dx,
      r.origY + resizeOffset.dy,
      r.origW + resizeOffset.dw,
      r.origH + resizeOffset.dh,
    );

    resizeRef.current = null;
    setResizeOffset(null);
    setIsResizing(false);
  }, [resizeOffset, onResizeEnd]);

  /**
   * Get the display rect for a section (original + resize offset if active).
   */
  const getSectionRect = useCallback(
    (
      sectionId: string,
      origX: number,
      origY: number,
      origW: number,
      origH: number,
    ) => {
      if (resizeOffset && resizeOffset.id === sectionId) {
        return {
          x: origX + resizeOffset.dx,
          y: origY + resizeOffset.dy,
          width: origW + resizeOffset.dw,
          height: origH + resizeOffset.dh,
        };
      }
      return { x: origX, y: origY, width: origW, height: origH };
    },
    [resizeOffset],
  );

  return {
    isResizing,
    getSectionRect,
    handleResizePointerDown,
    handleResizePointerMove,
    handleResizePointerUp,
  };
}
